import { Component, Host, h, Prop, Event, Method, EventEmitter, Element, Watch } from '@stencil/core';
import { beforeWrite, createPopper, Options } from '@popperjs/core';
import { floatingElements } from '../../utils/floating-elements.utils';
import { Animation } from '@ionic/core';
import { dropdownOpenAnimation } from './animations/open';
import { dropdownCloseAnimation } from './animations/close';
import { DISABLE_ANIMATION_VALUE } from '../../utils/animations';
import { ElementOrSelector, getElementReference } from '../../utils/types/common-props.types';

const ESCAPE_KEY = 'Escape';

export declare type Placement = 'top-start' | 'top-end' | 'bottom-end' | 'bottom-start' | 'right' | 'left' | 'auto';
const PLACEMENTS = {
  'top-start': 'top-start',
  'top-end': 'top-end',
  'bottom-end': 'bottom-end',
  'bottom-start': 'bottom-start',
  right: 'right-start',
  left: 'left-start',
  auto: 'auto',
};

interface EventListener {
  element: Element | Document | Window;
  event: string;
  handler: any;
}

/**
 * @slot  - Dropdown contents.
 */
@Component({
  tag: 'sy-dropdown',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Dropdown {
  private popper: any;
  private currentAnimation: Animation | undefined;

  private toggleByElement: Element | null;
  private containerElement: Element | null;

  private openedEventListeners: EventListener[] = [];
  private eventListeners: EventListener[] = [];

  private resizeObserver: ResizeObserver;
  private latestContainerHeight: number;

  /**
   * Internal boolean to indicate whether dropdown is opened.
   * Used to identify when the property `opened` was modified externally.
   */
  private isOpenedInternal: boolean;

  private opening: Promise<void> | undefined;
  private closing: Promise<void> | undefined;

  @Element() el: HTMLSyDropdownElement;

  /**
   * Element that toggle dropdown.
   */
  @Prop() toggleBy: ElementOrSelector | undefined;

  /**
   * Element that dropdown will be connected to.
   * If null the dropdown will be connected to the toggleBy element.
   */
  @Prop() container: ElementOrSelector | undefined;

  /**
   * Describes the preferred placement of the dropdown content.
   * The "auto" placements will choose the side with most space.
   * The placement may change to make de dropdown fit better.
   */
  @Prop({ reflect: true }) placement: Placement = 'bottom-start';

  /**
   * Positioning strategy.
   * Use fixed when it is inside a fixed container.
   */
  @Prop({ reflect: true }) display: 'absolute' | 'fixed' = 'absolute';

  /**
   * It indicates if the dropdown is open.
   *
   */
  @Prop({ mutable: true, reflect: true }) opened = false;

  /**
   * If true, the dropdown will not close by pressing the ESC key.
   */
  @Prop({ reflect: true }) ignoreEscape = false;

  /**
   * If true, the dropdown will not close with an outside click.
   */
  @Prop({ reflect: true }) ignoreOutsideClick = false;

  /**
   * If true, the dropdown will be the same width as the container element.
   */
  @Prop() sameContainerWidth = false;

  /**
   * Displaces the dropdown away from, or toward, the reference element in the direction of its placement.
   * A positive number displaces it further away, while a negative number lets it overlap the reference.
   */
  @Prop({ reflect: true }) offsetDistance = 2;

  /**
   * Overlay advanced configs. <https://popper.js.org/>
   */
  @Prop() popperConfig?: { [k: string]: any };

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   * If 'none', the dropdown won't have open animation.
   */
  @Prop() openAnimation?: ((element: HTMLElement) => Animation) | string;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   * If 'none', the dropdown won't have close animation.
   */
  @Prop() closeAnimation?: ((element: HTMLElement) => Animation) | string;

  /**
   * Disables all animations
   */
  @Prop() disableAnimations = false;

  /**
   * Fired when dropdown is opened.
   */
  @Event() syDropdownDidOpen: EventEmitter<void>;

  /**
   * Fired when dropdown is closed.
   */
  @Event() syDropdownDidClose: EventEmitter<void>;

  /**
   * Emitted when a dropdown starts to close.
   */
  @Event() syDropdownWillClose: EventEmitter<void>;

  /**
   * Emitted when a dropdown starts to open.
   */
  @Event() syDropdownWillOpen: EventEmitter<void>;

  async componentDidLoad() {
    await this.selectElementsWatcher();
  }

  componentDidRender() {
    if (this.opened) {
      this.addEscapeHandler();
      this.addOutsideClickHandler();
      if (this.popper) {
        this.popper.update();
      }
    }
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    this.removeListeners(this.openedEventListeners);
    this.removeListeners(this.eventListeners);
    this.resizeObserver?.disconnect();
  }

  @Watch('toggleBy')
  @Watch('container')
  async selectElementsWatcher(): Promise<void> {
    this.toggleByElement = getElementReference(this.el, this.toggleBy, true);
    this.containerElement = getElementReference(this.el, this.container);

    this.eventListeners?.length && this.removeListeners(this.eventListeners);
    await this.addToggleByHandler();
    this.handleContainerResize();
  }

  @Watch('opened')
  async openedWatcher(): Promise<void> {
    if (this.opened === this.isOpenedInternal) {
      return;
    }

    if (this.opened) {
      this.opening = this.open();
      return this.opening;
    }
    this.closing = this.close();
    return this.closing;
  }

  /**
   * Open dropdown
   * If 'instant' is true, the dropdown won't have open animation.
   */
  @Method()
  async open(instant = false): Promise<void> {
    if (this.closing !== undefined) {
      await this.closing;
    }
    if (this.isOpenedInternal) {
      this.opening = undefined;
      return;
    }
    this.opening = this.openDropdown(instant);
    return this.opening;
  }

  private async openDropdown(instant = false): Promise<void> {
    const popperConfig = this.getPopperConfig();
    this.syDropdownWillOpen.emit();
    await floatingElements.open(this.el);
    this.popper = createPopper(this.containerElement as HTMLElement, this.el, popperConfig);

    this.isOpenedInternal = true;
    this.opened = true;

    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    if (!instant && !this.disableAnimations) {
      this.currentAnimation = this.getOpenAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }

    this.syDropdownDidOpen.emit();
  }

  /**
   * Close dropdown
   * If 'instant' is true, the dropdown won't have close animation.
   */
  @Method()
  async close(instant = false): Promise<void> {
    if (this.opening !== undefined) {
      await this.opening;
    }
    if (!this.isOpenedInternal) {
      this.closing = undefined;
      return;
    }
    this.closing = this.closeDropdown(instant);
    return this.closing;
  }

  private async closeDropdown(instant = false): Promise<void> {
    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    this.syDropdownWillClose.emit();

    if (!instant && !this.disableAnimations) {
      this.currentAnimation = this.getCloseAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }

    if (this.popper) {
      this.popper.destroy();
    }

    this.isOpenedInternal = false;
    this.opened = false;

    this.removeListeners(this.openedEventListeners);

    floatingElements.close(this.el);
    this.syDropdownDidClose.emit();
  }

  /**
   * Toggle dropdown
   */
  @Method()
  async toggle(event?: any, instant = false): Promise<void> {
    const isInsideClick = Boolean(
      (event?.composedPath() || []).find((p) => {
        if (p instanceof Node) {
          return this.el.contains(p);
        }
      }),
    );

    if (this.opening !== undefined) {
      await this.opening;
    }

    if (this.closing !== undefined) {
      await this.closing;
    }

    if (!isInsideClick) {
      if (this.opened) {
        await this.close(instant);
      } else {
        await this.open(instant);
      }
    }
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      return this.openAnimation === DISABLE_ANIMATION_VALUE ? undefined : dropdownOpenAnimation(this.el);
    }
    const animationBuilder = this.openAnimation || dropdownOpenAnimation;
    return animationBuilder(this.el);
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      return this.closeAnimation === DISABLE_ANIMATION_VALUE ? undefined : dropdownCloseAnimation(this.el);
    }
    const animationBuilder = this.closeAnimation || dropdownCloseAnimation;
    return animationBuilder(this.el);
  }

  private handleContainerResize(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const currentHeight = entry.target.clientHeight;
        if (!this.latestContainerHeight) this.latestContainerHeight = currentHeight;
        else if (this.latestContainerHeight !== currentHeight && this.popper) {
          this.popper.update();
          this.latestContainerHeight = currentHeight;
        }
      }
    });

    if (this.containerElement) {
      this.resizeObserver.observe(this.containerElement);
    }
  }

  private async addToggleByHandler() {
    if (this.toggleByElement) {
      const handler = this.toggle.bind(this);
      this.eventListeners.push({ element: this.toggleByElement, event: 'click', handler });
      this.toggleByElement.addEventListener('click', handler);
    }
  }

  private escapeHandler(event) {
    if (this.ignoreEscape) return;
    if (event.key === ESCAPE_KEY) {
      this.close();
      const element = this.toggleByElement;
      (element as HTMLElement)?.focus();
    }
  }

  private addEscapeHandler() {
    if (this.ignoreEscape) return;

    const event = 'keyup';
    const handler = this.escapeHandler.bind(this);

    document.addEventListener(event, handler);
    this.openedEventListeners.push({ element: document, event, handler });
  }

  private outsideClickHandler(event) {
    const isInsideClick = Boolean(
      (event?.composedPath() || []).find((p) => {
        if (p instanceof Node) {
          return this.el.contains(p);
        }
      }),
    );

    const checkToggleButtonClick =
      this.toggleByElement &&
      (event?.composedPath() || []).some(
        (p: Node) => p.nodeType === Node.ELEMENT_NODE && p.isEqualNode(this.toggleByElement),
      );

    if (!isInsideClick && this.opened && !checkToggleButtonClick) {
      this.close();
    }
  }

  private addOutsideClickHandler() {
    if (this.ignoreOutsideClick) return;

    const event = 'click';
    const handler = this.outsideClickHandler.bind(this);

    document.addEventListener(event, handler, { capture: true });
    this.openedEventListeners.push({ element: document, event, handler });
  }

  private removeListeners(listeners: EventListener[]) {
    listeners.forEach((listener) => {
      listener.element.removeEventListener(listener.event, listener.handler);
    });
    listeners = [];
  }

  private getPopperConfig(): any {
    const modifiers: Options['modifiers'] = [
      {
        name: 'offset',
        options: {
          offset: [0, this.offsetDistance],
        },
      },
    ];

    if (this.sameContainerWidth) {
      const sameWidth = {
        name: 'sameWidth',
        enabled: true,
        phase: beforeWrite,
        requires: ['computeStyles'],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
          state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
        },
      };
      modifiers.push(sameWidth);
    }

    const defaultPopperConfig: any = {
      placement: PLACEMENTS[this.placement],
      strategy: 'fixed',
      modifiers,
    };

    return {
      ...defaultPopperConfig,
      ...this.popperConfig,
    };
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
